import * as React from 'react';
import { LocalizeContextProps, withLocalize } from 'react-localize-redux';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import Select, { ValueType } from 'react-select';
import { Button, Form, Input } from 'reactstrap';
import { Dispatch } from 'redux';
import urlencode from 'urlencode';
import { Privilege, Workspace } from '../../clients/aasServerClient.generated';
import { applicationOptions } from '../../stores/applications/selectors';
import { getAssignablePrivileges, hideGroups } from '../../stores/auth/selectors';
import { groupOptions } from '../../stores/groups/selectors';
import { isLoading } from '../../stores/navigation/selectors';
import { RootState } from '../../stores/store';
import {
    clearSingleWorkspace, copySingleWorkspace,
    createSingleWorkspace, fetchSingleWorkspace,
    newSingleWorkspace, saveSingleWorkspace
} from '../../stores/workspaces/actionCreators';
import { getSingleWorkspace } from '../../stores/workspaces/selectors';
import ApplicationsSelect from '../applications/ApplicationSelect';
import FormInput from '../common/FormInput';
import SideModal from '../common/modals/SideModal';
import GroupsSelect from '../groups/GroupSelect';
import { LoadingButton } from '../navigation/LoadingButton';
import LoadingScreen from '../navigation/LoadingScreen';
import WorkspaceProperties from './WorkspaceProperties';
import WorkspaceSelect from './WorkspaceSelect';

interface Props extends LocalizeContextProps, RouteComponentProps<{ id: string | undefined }> {
    isLoading: boolean;
    workspace: {
        loaded: boolean;
        saved: boolean;
        workspace?: Partial<Workspace>;
    };

    groups: Array<{ label: string, value: string }>;
    hideGroups?: boolean;

    copySingleWorkspace: (id: string) => void;
    newSingleWorkspace: () => void;
    clearSingleWorkspace: () => void;
    createSingleWorkspace: (
        workspace: Partial<Workspace>,
        parentId?: string,
        groups?: Array<{ id: string, value: boolean }>,
    ) => void;
}

interface State {
    workspace?: Partial<Workspace>;
    groupSelection?: ValueType<{ label: string, value: string }>;
    parentSelection?: { label: string, value: string };
    saved: boolean;
    isCopy: boolean;
}

class NewWorkspacePage extends React.Component<Props, State> {

    public constructor(props: Props) {
        super(props);

        const id = this.props.match.params.id;
        if (id) {
            this.state = {
                isCopy: true,
                workspace: undefined,
                saved: false,
            };
            this.props.clearSingleWorkspace();
            this.props.copySingleWorkspace(id);
        } else {
            this.state = {
                isCopy: false,
                workspace: undefined,
                saved: false,
            };
            this.props.clearSingleWorkspace();
            this.props.newSingleWorkspace();
        }
    }

    public componentDidUpdate(prevProps: Props) {
        if (!prevProps.workspace.loaded && this.props.workspace.loaded) {
            this.setState({
                workspace: this.props.workspace.workspace,
            });
        }
        if (!prevProps.workspace.saved && this.props.workspace.saved) {
            this.closePage();
        }
    }
    public modalRef = React.createRef<HTMLDivElement>();
    public render() {
        const t = this.props.translate;
        return (
            <SideModal
                isOpen={true}
                toggle={this.closePage}
                headerText={this.state.isCopy ? t('workspaces.edit.header.copy') as string
                    : t('workspaces.edit.header.add') as string}
                footer={this.renderFooter()}
                objectRef={this.modalRef}                
            >
                {this.props.workspace.loaded && this.props.workspace.workspace ? (
                    <Form>
                        <h3 className="mb-4 mt-2">{t('workspaces.edit.section.details')}</h3>
                        <WorkspaceProperties
                            type="new"
                            workspace={this.props.workspace.workspace}
                            updateWorkspace={this.updateWorkspace}
                            saved={this.state.saved}
                        />
                        {this.props.workspace.workspace!.parent ? (
                            <React.Fragment>
                                <hr className="fill-modal" />
                                <h3 className="mb-4 mt-4">{t('workspaces.edit.section.parent')}</h3>
                                <WorkspaceSelect
                                    selected={this.props.workspace.workspace.parent.id}
                                    updateSelection={this.updateParentSelection}
                                    selection={this.state.parentSelection}
                                />
                            </React.Fragment>
                        ) : null}
                        {this.props.hideGroups === false && this.props.workspace.workspace!.groups ? (
                            <React.Fragment>
                                <hr className="fill-modal" />
                                <h3 className="mb-4 mt-4">{t('workspaces.edit.section.groups')}</h3>
                                <GroupsSelect
                                    selected={this.props.workspace.workspace.groups.map((group) => group.id)}
                                    updateSelection={this.updateGroupSelection}
                                    selection={this.state.groupSelection}
                                    filterPerWorkspace= {false}
                                />
                            </React.Fragment>
                        ) : null}
                    </Form>
                ) : (
                        <LoadingScreen text={t('loading.loading') as string} />
                    )}
            </SideModal>
        );
    }

    private updateWorkspace = (workspace: Partial<Workspace>) => {
        this.setState({
            workspace,
        });
    }

    private updateGroupSelection = (selection: ValueType<{ label: string, value: string }>) => {
        this.setState({ groupSelection: selection });
    }

    private updateParentSelection = (selection: ValueType<{ label: string, value: string }> |
    { label: string, value: string }) => {
        this.setState({ parentSelection: selection as { label: string, value: string } });
    }

    private closePage = () => {
        this.props.history.push('/workspaces');
    }

    private renderFooter = (): React.ReactNode => {
        const t = this.props.translate;
        return (
            <React.Fragment>
                <LoadingButton
                    loading={this.props.isLoading}
                    color="success"
                    onClick={this.createWorkspace}
                    style={{ width: '200px' }}
                >
                    {t('workspaces.edit.button.save')}
                </LoadingButton>
            </React.Fragment>
        );
    }

    private createWorkspace = () => {
        const workspace = this.state.workspace;
        let groups: Array<{ id: string; value: boolean }> | undefined;
        if (this.state.groupSelection) {
            groups = this.props.groups.map((group) => ({
                id: group.value,
                // tslint:disable-next-line:no-any
                value: (this.state.groupSelection as any[]).find((x) => x.value === group.value) !== undefined,
            }));
        }

        this.setState({ saved: true });
        const workspaceId = this.state.parentSelection?.value;
        this.props.createSingleWorkspace(workspace!, workspaceId, groups);
    }
}

const mapStateToProps = (state: RootState) => ({
    applications: applicationOptions(state),
    groups: groupOptions(state),
    hideGroups: hideGroups(state),
    isLoading: isLoading(state),
    workspace: getSingleWorkspace(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    clearSingleWorkspace: () => dispatch(clearSingleWorkspace()),
    copySingleWorkspace: (id: string) => dispatch(copySingleWorkspace(id)),
    createSingleWorkspace: (
        workspace: Partial<Workspace>,
        parentId?: string,
        groups?: Array<{ id: string, value: boolean }>,
    ) => dispatch(createSingleWorkspace(workspace, parentId, groups)),
    newSingleWorkspace: () => dispatch(newSingleWorkspace()),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(withLocalize(NewWorkspacePage)));